/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponentsAddRequests.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-09-2024
 * @description : Displays a list of pending, approved, and rejected requests for adding a vehicle component
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Box, IconButton } from "@mui/material";
import { RejectAddVcDialog, ApproveAddVcDialog } from "../../components";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import DoneIcon from "@mui/icons-material/Done";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch, useSelector } from "react-redux";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import {
  adminSettingsAPI,
  HandleApiActions,
  userManagementApis,
} from "../../redux/actions";
import helper from "../../utils/helper";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import CustomFilterV2 from "../../components/customComponentsV2/common/filter/CustomFilterV2";
import CustomRadioButtonV2 from "../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import sortUp from "../../assets/sortup.png";
import sortDown from "../../assets/sortdown.png";

function VehicleComponentsAddRequests({ totalItems }) {
  /**
   * ===================== State Initialization =====================
   */
  /** ============== Vehicle Systems ============== **/
  // removed unused `requestStatusList`
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  const vcAddRequests = useSelector(
    (state) => state.adminSettingsReducer.vcAddRequests,
  );
  const { add_vc_request_status } = useSelector(
    (state) => state?.settingsReducer?.metaData,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  /** ============== Filter V2 ============== **/
  const [anchorElFilterV2, setAnchorElFilterV2] = useState(null);
  const [openFilterV2, setOpenFilterV2] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: "requested_at",
    sortOrder: "Sort Asc -Des",
    status: {
      all: true,
      pending: true,
      approved: true,
      rejected: true,
    },
  });

  /** ============== Filter (Old - for backward compatibility) ============== **/
  // removed unused old filter dropdown state
  const [selectedSearchField, setSelectedSearchField] = useState("status");
  const [searchFieldValue, setSearchFieldValue] = useState(100); // Default pending should be selected
  // removed unused searchQuery state
  /** ============== Pagination ============== **/
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalVcAddRequestsCount, setTotalVcAddRequestsCount] = useState(0);
  /** ============== Approve/Reject add vehicle component dialog ============== **/
  const [selectedAddVcId, setSelectedAddVcId] = useState(null);
  const [openAddVcRejectDialog, setOpenAddVcRejectDialog] = useState(false);
  const [openAddVcApproveDialog, setOpenAddVcApproveDialog] = useState(false);
  /** ============== Data Grid Table ============== **/
  const [tableRowsData, setTableRowsData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [userDetailsMap, setUserDetailsMap] = useState(new Map());
  const [userDetailsLoaded, setUserDetailsLoaded] = useState(false);

  // Toggle row expansion
  const handleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };
  const getUserDisplayName = (userId, fallbackDetails) => {
    if (!userId && fallbackDetails?.fullname) return fallbackDetails.fullname;
    const key = userId !== undefined && userId !== null ? String(userId) : "";
    return (
      userDetailsMap?.get(key)?.fullname || fallbackDetails?.fullname || "-"
    );
  };

  const COLUMNS = [
    {
      field: "expand",
      headerName: "",
      flex: 0.2,
      minWidth: 40,
      sortable: false,
      renderCell: (params) => {
        // Don't show expand button for detail rows
        if (params.row.isDetailRow) return null;

        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowExpansion(params.row.id);
            }}
            sx={{ p: 0.5 }}
            disabled={!userDetailsLoaded}
          >
            {expandedRows[params.row.id] ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: "vehicle_system_details",
      headerName: "Vehicle System",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        // For detail rows, show expanded information
        if (params.row.isDetailRow) {
          const data = params.row.parentData;
          return (
            <Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Requested By:
                </Typography>
                <Typography variant="body2">
                  Name: {data?.requested_user_details?.fullname || "-"}
                </Typography>
                <Typography variant="body2">
                  Email: {data?.requested_user_details?.email || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Requested Reason:
                </Typography>
                <Typography variant="body2">
                  {data?.requested_reason || "-"}
                </Typography>
              </Box>
            </Box>
          );
        }

        return (
          <Typography variant="body2">
            {params?.row?.vehicle_system_details?.name || "-"}
          </Typography>
        );
      },
    },
    {
      field: "pr_vc_name",
      headerName: "Vehicle Component",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;
        return (
          <Typography variant="body2">
            {params?.row?.pr_vc_name || "-"}
          </Typography>
        );
      },
    },
    {
      field: "project_details",
      headerName: "Project Details",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (params?.row?.isDetailRow) {
          const data = params?.row?.parentData;
          const approverName = getUserDisplayName(
            data?.approved_rejected_by,
            data?.approved_rejected_user_details,
          );
          return (
            <Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Approved/Rejected By:
                </Typography>
                <Typography variant="body2">Name: {approverName}</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Approved/Rejected Reason:
                </Typography>
                <Typography variant="body2">
                  {data?.approved_rejected_reason || "-"}
                </Typography>
              </Box>
            </Box>
          );
        }
        const variant = params?.row?.variant_details?.name || "-";
        const truncatedVariant =
          variant.length > 20 ? variant.substring(0, 20) + "..." : variant;

        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Name: {params?.row?.project_details?.name || "-"}
            </Typography>
            <Typography
              variant="body2"
              title={variant}
            >
              Variant: {truncatedVariant}
            </Typography>
            <Typography variant="body2">
              Trade Mark: {params?.row?.project_details?.trade_mark || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "requested_at",
      headerName: "Requested At",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;

        return (
          <Typography variant="body2">
            {params?.row?.requested_at || "-"}
          </Typography>
        );
      },
    },
    {
      field: "status_data",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;
        const status = params?.row?.status_data;
        const getStatusColor = () => {
          if (status === "PENDING") return "#000000";
          if (status === "APPROVED") return "#26890D";
          if (status === "REJECTED") return "#DC2626";
          return "#666";
        };

        return (
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(),
              fontStyle: "Open Sans",
              fontSize: "13px",
              lineHeight: "20px",
              letterSpacing: "0px",
            }}
          >
            {status
              ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
              : "-"}
          </Typography>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;
        return params?.row?.status === 100 ? (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5, fontWeight: "bold" }}
              onClick={(e) => {
                e.stopPropagation();
                handleApproveAddVcRequest({
                  id: params?.row?.id,
                });
              }}
            >
              <DoneIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleRejectAddVcRequest({
                  id: params?.row?.id,
                });
              }}
            >
              <BlockIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2">—</Typography>
        );
      },
    },
  ];

  /**
   * ===================== Effect Hooks =====================
   */
  // Fetches vehicle component add requests when any of the dependencies change.
  // Dependencies: pagination, search filters
  // (moved) Fetch effect placed after callback definition

  // Resets filters/patination when any of the dependencies change.
  // Dependencies:
  useEffect(() => {
    // Reset filters/pagination on initial mount
    handleFilterChange("status", "");
    setRowsPerPage(25);
    setPage(1);
  }, []);
  // Checks if there is a next page
  // filtered vehicle component add requests list for data grid table
  // Dependencies: vcAddRequests
  // Generate rows with expanded detail rows
  const generateTableRows = () => {
    const rows = [];
    tableRowsData?.forEach((item, index) => {
      // Main row
      rows.push({
        ...item,
        id: item.id || index,
        isMainRow: true,
      });

      // Expanded detail row
      if (expandedRows[item.id] && userDetailsLoaded) {
        rows.push({
          id: `${item.id}-detail`,
          isDetailRow: true,
          parentData: item,
        });
      }
    });
    return rows;
  };

  const finalTableRows = generateTableRows();

  useEffect(() => {
    // filtered vehicle component add requests list for data grid table
    const filteredVcAddRequests = vcAddRequests?.map((item, index) => {
      return {
        ...item,
        sNo: (page - 1) * rowsPerPage + index + 1,
        status_data:
          helper?.getValueByKey(add_vc_request_status, item?.status) || "-",
        requested_at: convertUtcToTimeZone(
          item?.requested_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        approved_rejected_reason: item?.approved_rejected_reason || "-",
        approved_rejected_at: convertUtcToTimeZone(
          item?.approved_rejected_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
      };
    });
    setTableRowsData(filteredVcAddRequests);
  }, [
    vcAddRequests,
    page,
    rowsPerPage,
    add_vc_request_status,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  /**
   * ===================== API Call Functions =====================
   */
  /**
   * @author : Narender
   * @Date : 20-03-2025
   * @description : Get vehicle component add requests
   * @param {page_number, page_size}
   * @return {msg: "", data: [], error: true | false}
   */
  const getVcAddRequests = React.useCallback(async () => {
    const apiDetails = adminSettingsAPI.get_vc_add_requests;

    // Determine sort order (ascending or descending)
    const sortOrderMap = {
      "Sort Asc -Des": "asc",
      "Sort Des-Asc": "desc",
    };

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          ...(searchFieldValue && {
            [selectedSearchField]: searchFieldValue,
          }),
          page_number: page,
          page_size: rowsPerPage,
          sort_by: filters.sortBy,
          sort_order: sortOrderMap[filters.sortOrder],
        },
      }),
    );
    if (resp.error) return;
    setTotalVcAddRequestsCount(resp?.total_count || 0);
  }, [
    dispatch,
    filters.sortBy,
    filters.sortOrder,
    selectedSearchField,
    searchFieldValue,
    page,
    rowsPerPage,
  ]);

  // Fetch when dependencies change using a stable callback
  useEffect(() => {
    getVcAddRequests();
  }, [getVcAddRequests]);

  const fetchUserDetails = React.useCallback(async () => {
    try {
      const apiDetails = { ...userManagementApis?.get_users_list_pa };
      delete apiDetails.action_type;
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            page_size: 10000,
            page_number: 1,
          },
          show_toast: false,
        }),
      );

      if (resp.error) {
        setUserDetailsLoaded(true);
        return;
      }

      const users = resp?.data || [];
      const userMap = new Map(
        users?.map((user) => [String(user?.user_id), user]),
      );
      setUserDetailsMap(userMap);
      setUserDetailsLoaded(true);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      setUserDetailsLoaded(true);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  /**
   * ===================== UI & State Management Functions =====================
   */
  /** ============== Data Grid Table ============== **/
  // Row click event in the data grid table
  const handleTableRowClick = ({ params }) => {};
  // Approve action for adding a vehicle component
  const handleApproveAddVcRequest = (params) => {
    setSelectedAddVcId(params?.id);
    setOpenAddVcApproveDialog(true);
  };
  // Open the rejection dialog for adding a vehicle component
  const handleRejectAddVcRequest = (params) => {
    setSelectedAddVcId(params?.id);
    setOpenAddVcRejectDialog(true);
  };
  /** ============== Filter ============== **/
  // Opens the filter dropdown
  // Updates filter state and resets page
  const handleFilterChange = (searchField, searchValue) => {
    setSelectedSearchField(searchField);
    setSearchFieldValue(searchValue);
    setPage(1);
  };
  // Applies the selected filter
  // removed unused handleFilterApplyNow
  // Resets filter to default
  // removed handleFilterResetAll (handled inline in useEffect)

  /** ============== Filter V2 Handlers ============== **/
  const handleFilterV2Click = (event) => {
    setAnchorElFilterV2(event.currentTarget);
    setOpenFilterV2(true);
  };

  const handleFilterV2Close = () => {
    setAnchorElFilterV2(null);
    setOpenFilterV2(false);
  };

  const handleFilterV2Button = (action) => {
    if (action === "apply") {
      applyFiltersV2();
    } else {
      resetFiltersV2();
    }
  };

  const resetFiltersV2 = () => {
    setFilters({
      sortBy: "requested_at",
      sortOrder: "Sort Asc -Des",
      status: {
        all: true,
        pending: true,
        approved: true,
        rejected: true,
      },
    });
    handleFilterV2Close();
    setPage(1);
    // Clear status search so API fetches all
    setSearchFieldValue("");
    // Fetch with default filters
    getVcAddRequests();
  };

  const applyFiltersV2 = async () => {
    handleFilterV2Close();

    // Determine status filter value
    let statusValue = null;
    if (
      filters.status.pending &&
      !filters.status.approved &&
      !filters.status.rejected
    ) {
      statusValue = 100;
    } else if (
      !filters.status.pending &&
      filters.status.approved &&
      !filters.status.rejected
    ) {
      statusValue = 200;
    } else if (
      !filters.status.pending &&
      !filters.status.approved &&
      filters.status.rejected
    ) {
      statusValue = 300;
    }

    // Update the search field value and page
    setPage(1);
    setSearchFieldValue(statusValue ?? "");

    // Determine sort order (ascending or descending)
    const sortOrderMap = {
      "Sort Asc -Des": "asc",
      "Sort Des-Asc": "desc",
    };

    // Make the API call directly with current filter values
    const apiDetails = adminSettingsAPI.get_vc_add_requests;
    await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          ...(statusValue !== null && {
            status: statusValue,
          }),
          page_number: 1,
          page_size: rowsPerPage,
          sort_by: filters.sortBy,
          sort_order: sortOrderMap[filters.sortOrder],
        },
      }),
    );
  };

  /** ============== Pagination ============== **/
  // Resets pagination to default
  // removed handlePaginationReset (handled inline in useEffect)

  return (
    <Box>
      {/* ============== Filter and Search Bar ============== */}
      <Box sx={styles.filterSearchContainer}>
        {/* New Filter V2 */}
        <CustomFilterV2
          anchorEl={anchorElFilterV2}
          open={openFilterV2}
          widthPerColumn={"210px"}
          handleClick={handleFilterV2Click}
          handleFilterButton={handleFilterV2Button}
          handleClose={handleFilterV2Close}
          sections={[
            {
              heading: "Sorting",
              content: (
                <Box>
                  <Box sx={{ pl: 3, pb: 1.5 }}>
                    <CustomRadioButtonV2
                      spacing={3}
                      value={filters.sortBy}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          sortBy: e.target.value,
                        }))
                      }
                      data={[
                        { value: "requested_at", label: "Requested At" },
                        {
                          value: "approved_rejected_at",
                          label: "Approved/Rejected At",
                        },
                      ]}
                      sx={{
                        "& .MuiFormControlLabel-label": { fontSize: "14px" },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          sortOrder: "Sort Asc -Des",
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setFilters((prev) => ({
                            ...prev,
                            sortOrder: "Sort Asc -Des",
                          }));
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 0,
                        backgroundColor:
                          filters?.sortOrder === "Sort Asc -Des"
                            ? "#26890D"
                            : "transparent",
                        color:
                          filters?.sortOrder === "Sort Asc -Des"
                            ? "#FFFFFF"
                            : "#000000",
                      }}
                    >
                      <img
                        src={sortUp}
                        alt="Sort Asc -Des"
                        style={{ width: 16, height: 16 }}
                      />
                      <Typography variant="body2" sx={{ color: "inherit" }}>
                        Sort Asc -Des
                      </Typography>
                    </Box>

                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          sortOrder: "Sort Des-Asc",
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setFilters((prev) => ({
                            ...prev,
                            sortOrder: "Sort Des-Asc",
                          }));
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 0,
                        width: "100%",
                        backgroundColor:
                          filters?.sortOrder === "Sort Des-Asc"
                            ? "#26890D"
                            : "transparent",
                        color:
                          filters?.sortOrder === "Sort Des-Asc"
                            ? "#FFFFFF"
                            : "#000000",
                      }}
                    >
                      <img
                        src={sortDown}
                        alt="Sort Des-Asc"
                        style={{ width: 16, height: 16 }}
                      />
                      <Typography variant="body2" sx={{ color: "inherit" }}>
                        Sort Des-Asc
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                  </Box>
                </Box>
              ),
            },
            {
              heading: "Filter by Status",
              content: (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <CustomCheckBoxV2
                    value={filters.status.all}
                    onChange={() =>
                      setFilters((prev) => {
                        const newAllValue = !prev.status.all;
                        return {
                          ...prev,
                          status: {
                            all: newAllValue,
                            // When selecting "All", select all specific statuses
                            // When unselecting "All", default to Pending selected
                            pending: true,
                            approved: newAllValue,
                            rejected: newAllValue,
                          },
                        };
                      })
                    }
                    label="All"
                  />
                  <CustomCheckBoxV2
                    value={filters.status.pending}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        status: {
                          all: false,
                          pending: true,
                          approved: false,
                          rejected: false,
                        },
                      }))
                    }
                    label="Pending"
                  />
                  <CustomCheckBoxV2
                    value={filters.status.approved}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        status: {
                          all: false,
                          pending: false,
                          approved: true,
                          rejected: false,
                        },
                      }))
                    }
                    label="Approved"
                  />
                  <CustomCheckBoxV2
                    value={filters.status.rejected}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        status: {
                          all: false,
                          pending: false,
                          approved: false,
                          rejected: true,
                        },
                      }))
                    }
                    label="Rejected"
                  />
                </Box>
              ),
            },
          ]}
        />

        {/* Search Bar */}
        <Box>
          <CustomSearchbarWithApiV2
            params={{
              page_number: page,
              page_size: rowsPerPage,
            }}
            apiAction={adminSettingsAPI.get_vc_add_requests}
            searchKey={"name"}
            getDataOnSearch={(data, resp) => {
              setTotalVcAddRequestsCount(resp?.total_count || 0);
            }}
            placeholder={"Search"}
          />
        </Box>
      </Box>
      {/* ============== Data Grid Table ============== */}
      <Box
        sx={{
          height: "68vh", // ✅ Takes 70% of screen height
          width: "100%",
          display: "flex",
          flexDirection: "column",
          mt: 0.5,
        }}
      >
        <CustomDataGridV2
          showPagination={true}
          rows={finalTableRows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalVcAddRequestsCount}
          handlePageChange={(newPage) => setPage(newPage)} // Convert back to 1-based
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={handleTableRowClick}
          getRowHeight={() => "auto"}
        />
      </Box>
      {/* ============== Approve/Reject add vehicle component dialog ============== */}
      <ApproveAddVcDialog
        openDialog={openAddVcApproveDialog}
        setOpenDialog={setOpenAddVcApproveDialog}
        title={"Approve Reason"}
        selectedAddVcId={selectedAddVcId}
        setSelectedAddVcId={setSelectedAddVcId}
        getVcAddRequests={getVcAddRequests}
      />
      <RejectAddVcDialog
        openDialog={openAddVcRejectDialog}
        setOpenDialog={setOpenAddVcRejectDialog}
        title={"Reject Reason"}
        selectedAddVcId={selectedAddVcId}
        setSelectedAddVcId={setSelectedAddVcId}
        getVcAddRequests={getVcAddRequests}
      />
    </Box>
  );
}

export default VehicleComponentsAddRequests;

VehicleComponentsAddRequests.propTypes = {
  totalItems: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

// Styles
const styles = {
  txtBold: {
    fontWeight: "bold",
  },
  filterSearchContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 2,
    mb: 0.5,
    flexWrap: "wrap",
    mt: -2.5,
  },
};
